/**
 * Mappers between the Phase 1 backend SearchItem / RecentFood shapes and
 * the legacy FoodConfirm.jsx prop shape. Centralized here so every tab
 * uses the same conversion logic.
 */

import { logServingDebug } from './servingDebug';

// Secondary nutrients carried when the provider supplied them (absent = unknown).
const MICRO_KEYS = ['sodium_mg', 'sugar_g', 'fiber_g'];

/**
 * Convert a normalized SearchItem (from /search/* endpoints) into the
 * `food` prop shape expected by FoodConfirm.jsx.
 *
 * FoodConfirm's convention: `nutrition` is per-1-unit of `serving_unit`,
 * and the displayed quantity is multiplied against it. Backend FatSecret
 * items return macros for the FULL serving (e.g. 195 cal per 100g), so we
 * must divide by `serving.size` here to fit the per-unit contract. We
 * preserve the natural serving size so FoodConfirm can default the
 * quantity to e.g. 100 (grams) rather than 1.
 *
 * `serving_options` (Feb 2026) — when the backend provides multiple
 * serving choices (e.g. "100 g" / "1 oz" / "1 breast" for chicken),
 * forward them all so FoodConfirm's unit picker can let the user switch.
 * Each option carries its own per-1-unit macros, so the picker can update
 * the displayed totals locally without an extra round-trip.
 */
export function searchItemToFood(item) {
  if (!item) return null;
  const macros = item.macros || {};
  const serving = item.serving || {};
  const rawSize = typeof serving.size === 'number' && serving.size > 0 ? serving.size : 1;
  // Per-unit divisor: only divide when the serving is > 1 unit (e.g. 100 g),
  // otherwise we keep the macros as-is for "1 serving" / "1 piece" foods.
  const divisor = rawSize > 1 ? rawSize : 1;
  const rawOptions = Array.isArray(item.serving_options) ? item.serving_options : [];
  const mappedOptions = rawOptions.map(function (opt) {
    const optMacros = opt.macros || {};
    // Secondary nutrients (sodium/sugar/fiber) are already per-1-unit from the
    // backend. Forward them ONLY when present — absence must stay `undefined`,
    // never become 0, so the UI can tell "unknown" apart from a real zero.
    const optNutrition = {
      calories: Number(optMacros.calories || 0),
      protein_g: Number(optMacros.protein || 0),
      carbs_g: Number(optMacros.carbs || 0),
      fat_g: Number(optMacros.fat || 0),
    };
    if (optMacros.sodium_mg != null) optNutrition.sodium_mg = Number(optMacros.sodium_mg);
    if (optMacros.sugar_g != null) optNutrition.sugar_g = Number(optMacros.sugar_g);
    if (optMacros.fiber_g != null) optNutrition.fiber_g = Number(optMacros.fiber_g);
    return {
      unit: opt.unit,
      size: typeof opt.size === 'number' && opt.size > 0 ? opt.size : 1,
      description: opt.description || null,
      // Convert backend's {calories,protein,carbs,fat} into the
      // {calories,protein_g,carbs_g,fat_g} shape FoodConfirm expects so
      // it can swap `currentNutrition` in one assignment when the user
      // picks a different unit.
      nutrition: optNutrition,
    };
  });
  // Top-level nutrition is per-1-unit of serving_unit; the backend's top-level
  // macros are the FULL default serving, so divide by the serving size. Micros
  // follow the same divisor and are forwarded only when present (never 0).
  const topNutrition = {
    calories: Number(macros.calories || 0) / divisor,
    protein_g: Number(macros.protein || 0) / divisor,
    carbs_g: Number(macros.carbs || 0) / divisor,
    fat_g: Number(macros.fat || 0) / divisor,
  };
  if (macros.sodium_mg != null) topNutrition.sodium_mg = Number(macros.sodium_mg) / divisor;
  if (macros.sugar_g != null) topNutrition.sugar_g = Number(macros.sugar_g) / divisor;
  if (macros.fiber_g != null) topNutrition.fiber_g = Number(macros.fiber_g) / divisor;
  const out = {
    external_id: item.external_id || item.id,
    name: item.name,
    brand: item.brand || null,
    restaurant: item.restaurant || null,
    image_url: item.image_url || null,
    serving_size: rawSize,
    serving_unit: serving.unit || 'serving',
    serving_description: serving.description || null,
    serving_options: mappedOptions,
    nutrition: topNutrition,
    source: item.source || 'CUSTOM',
    ingredients: [],
  };
  logServingDebug('searchItemToFood', {
    in: { external_id: item.external_id || item.id, macros: item.macros, serving: item.serving,
          serving_options: item.serving_options },
    divisor: divisor,
    out: { serving_size: out.serving_size, serving_unit: out.serving_unit,
           serving_description: out.serving_description, nutrition: out.nutrition,
           serving_options: out.serving_options },
  });
  return out;
}

/**
 * Convert a RecentFood (from /recent-foods) into the FoodConfirm `food` shape.
 *
 * Recent rows ship the MACROS scaled to the user's most recent log (e.g.
 * 200 g of chicken → 390 cal), plus `quantity_logged` (the qty that
 * produced those macros) and `serving_size` (the food's natural unit).
 * FoodConfirm consumes macros as per-1-unit, so we divide here. The
 * tapped recent then opens with the same quantity the user picked last
 * time — making tap-to-relog a single confirm tap when nothing changed.
 *
 * Backwards-compatibility: legacy recents without `quantity_logged` fall
 * back to `serving_size` so the displayed totals match the stored macros.
 */
export function recentFoodToFood(recent) {
  if (!recent) return null;
  const macros = recent.macros || {};
  const servingSize = typeof recent.serving_size === 'number' && recent.serving_size > 0
    ? recent.serving_size
    : 1;
  const loggedQty = typeof recent.quantity_logged === 'number' && recent.quantity_logged > 0
    ? recent.quantity_logged
    : servingSize;
  // Per-unit divisor uses the qty that produced the stored macros so the
  // numbers stay accurate regardless of how the user scaled last time.
  const divisor = loggedQty > 0 ? loggedQty : 1;
  const recentNutrition = {
    calories: Number(macros.calories || 0) / divisor,
    protein_g: Number(macros.protein || 0) / divisor,
    carbs_g: Number(macros.carbs || 0) / divisor,
    fat_g: Number(macros.fat || 0) / divisor,
  };
  // Stored sodium/sugar/fiber come back only when they were logged; same
  // divisor as the macros. Missing stays undefined (never 0). Keys inlined:
  // recentFoodToFood.node.test.js runs this function standalone.
  ['sodium_mg', 'sugar_g', 'fiber_g'].forEach(function (k) {
    if (macros[k] != null) recentNutrition[k] = Number(macros[k]) / divisor;
  });
  return {
    external_id: recent.external_id || '',
    name: recent.name,
    // Restaurant/brand identity from the most recent log, so a re-log stays
    // attributed to its chain/brand.
    brand: recent.brand || null,
    restaurant: recent.restaurant || null,
    image_url: recent.image_url || null,
    serving_size: loggedQty,
    serving_unit: recent.serving_unit || 'serving',
    serving_description: recent.serving_description || null,
    serving_options: [],
    nutrition: recentNutrition,
    source: recent.source || 'CUSTOM',
    ingredients: [],
  };
}

/**
 * Choose an emoji avatar for a food row based on its category/brand/restaurant.
 * Deterministic so the same food always renders with the same emoji.
 */
// Copy only the secondary nutrients that are actually present (never 0-fill).
function copyPresentMicros(target, source) {
  if (!source) return target;
  MICRO_KEYS.forEach(function (k) {
    if (source[k] != null && target[k] == null) target[k] = Number(source[k]);
  });
  return target;
}

/**
 * Merge freshly-hydrated FatSecret detail into a known list/recent food WITHOUT
 * ever discarding known macros. Used when a restaurant/recent item (which
 * already carries real calories/macros) is enriched with full serving detail:
 * if the hydrated record comes back with zero calories (or missing nutrition),
 * we keep the known values so a 380-cal item can never become a 0-cal log.
 * Serving options from the richer record are preferred when present.
 */
export function mergeHydratedFood(known, hydrated) {
  if (!hydrated) return known;
  if (!known) return hydrated;
  let knownN = known.nutrition || {};
  // When the known serving stays active, it may still lack sodium/sugar/fiber
  // that hydration supplied. Per-unit values are only comparable for the SAME
  // unit, so borrow micros from the hydrated serving with the known unit
  // (option first, else the hydrated default serving). Known values win;
  // core macros are never touched. A different unit contributes nothing.
  const knownUnit = known.serving_unit;
  const sameUnitOpt = !knownUnit ? null : (Array.isArray(hydrated.serving_options) ? hydrated.serving_options : [])
    .find(function (o) { return o && o.unit === knownUnit; });
  const sameUnitN = sameUnitOpt
    ? sameUnitOpt.nutrition
    : (knownUnit && hydrated.serving_unit === knownUnit ? hydrated.nutrition : null);
  knownN = copyPresentMicros(Object.assign({}, knownN), sameUnitN);
  const freshN = hydrated.nutrition || {};
  const knownHasMacros = Number(knownN.calories || 0) > 0;
  // Start from the hydrated record for its richer metadata + serving_options,
  // then let the KNOWN food's active serving win. The hydrated response's
  // default serving (e.g. 100 g) must NEVER replace the known active serving
  // (e.g. "1 small breast") merely because the hydrated record has valid macros.
  const merged = Object.assign({}, hydrated);
  merged.serving_options = (Array.isArray(hydrated.serving_options) && hydrated.serving_options.length > 0)
    ? hydrated.serving_options
    : (known.serving_options || []);
  if (knownHasMacros) {
    // The known/original active serving + its macros remain the active selection.
    merged.nutrition = knownN;
    merged.serving_size = known.serving_size;
    merged.serving_unit = known.serving_unit;
    merged.serving_description = known.serving_description || hydrated.serving_description;
    // Ensure the active serving is a selectable option so the confirm screen
    // keeps it as the default instead of the hydrated record's 100 g serving.
    const hasKnownUnit = (merged.serving_options || []).some(function (o) { return o.unit === known.serving_unit; });
    if (known.serving_unit && !hasKnownUnit) {
      merged.serving_options = [{
        unit: known.serving_unit,
        size: (typeof known.serving_size === 'number' && known.serving_size > 0) ? known.serving_size : 1,
        description: known.serving_description || null,
        nutrition: knownN,
      }].concat(merged.serving_options || []);
    }
  } else if (!(Number(freshN.calories || 0) > 0)) {
    // Neither side has usable macros: keep whatever the known item had.
    merged.nutrition = knownN;
    merged.serving_size = known.serving_size;
    merged.serving_unit = known.serving_unit;
    merged.serving_description = known.serving_description || hydrated.serving_description;
  }
  logServingDebug('mergeHydratedFood', {
    known_has_macros: knownHasMacros,
    known: { serving_size: known.serving_size, serving_unit: known.serving_unit, nutrition: knownN },
    hydrated: { serving_size: hydrated.serving_size, serving_unit: hydrated.serving_unit,
                nutrition: freshN, serving_options: hydrated.serving_options },
    out: { serving_size: merged.serving_size, serving_unit: merged.serving_unit,
           nutrition: merged.nutrition, serving_options: merged.serving_options },
  });
  return merged;
}

/**
 * Is this recent-foods row a GROUPED meal (AI photo log or multi-food basket
 * meal)? Grouped recents must open the grouped CustomMealView detail (combined
 * macros + component rows) and re-log as ONE grouped meal — never be flattened
 * into a single zero-calorie FoodConfirm entry.
 */
export function isGroupedRecent(recent) {
  if (!recent) return false;
  const src = String(recent.source || '').toUpperCase();
  const id = String(recent.external_id || recent.meal_id || '');
  return (
    src === 'AI_PHOTO_LOG' ||
    id.indexOf('ailog_group_') === 0 ||
    id.indexOf('grouped_') === 0 ||
    (typeof recent.ingredient_count === 'number' && recent.ingredient_count > 1)
  );
}

export function pickFoodEmoji(item) {
  if (!item) return '🍽️';
  const name = String(item.name || '').toLowerCase();
  const category = String(item.category || '').toUpperCase();
  if (item.restaurant) return '🏪';
  // BRANDED items previously rendered a 📦 box, which felt out of place
  // next to the food-themed plate/protein/grain icons used elsewhere
  // (e.g. "Pork Tenderloin" rightly shows 🍽️). For branded packaged
  // foods we now run the keyword detection first — "Chicken McNuggets"
  // still picks up 🍗, "Greek Yogurt" picks up 🥛, etc. — and only fall
  // back to the generic plate when nothing matches, instead of forcing
  // every branded item to look like a shipping box.
  if (category === 'MEAL') return '🍱';
  if (category === 'CUSTOM') return '📋';
  if (/water|drink|beverage|juice|coffee|tea/.test(name)) return '💧';
  if (/chicken|turkey|poultry/.test(name)) return '🍗';
  if (/beef|steak|burger|meat/.test(name)) return '🥩';
  if (/fish|salmon|tuna|cod|seafood/.test(name)) return '🐟';
  if (/egg/.test(name)) return '🥚';
  if (/rice|grain|quinoa|oat/.test(name)) return '🍚';
  if (/salad|lettuce|greens/.test(name)) return '🥗';
  if (/bread|toast|bagel|roll/.test(name)) return '🍞';
  if (/yogurt|milk|cheese/.test(name)) return '🥛';
  if (/apple|fruit|berry|banana/.test(name)) return '🍎';
  if (/burrito|wrap|taco/.test(name)) return '🌯';
  if (/pizza/.test(name)) return '🍕';
  if (/pasta|noodle/.test(name)) return '🍝';
  return '🍽️';
}

/**
 * Format a relative-time string for a recent log timestamp (ISO string).
 */
export function formatRecentTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const diffMs = Date.now() - then.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return minutes + 'm ago';
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + 'h ago';
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return days + 'd ago';
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return weeks + 'w ago';
  return then.toLocaleDateString();
}


/**
 * Known sodium/sugar/fiber for one basket component, scaled to its logged
 * quantity. Only nutrients present on the per-unit nutrition are returned.
 */
export function basketComponentMicros(nutrition, qty) {
  const out = {};
  const n = nutrition || {};
  MICRO_KEYS.forEach(function (k) {
    if (n[k] != null) out[k] = Math.round(Number(n[k]) * qty * 10) / 10;
  });
  return out;
}

/**
 * Grouped-meal sodium/sugar/fiber totals, ALL-OR-NOTHING per nutrient: a total
 * is returned only when EVERY component has that nutrient. If any component's
 * value is unknown the nutrient is omitted, so a partial sum is never stored
 * as though it were complete.
 */
export function groupedMicroTotals(components) {
  const out = {};
  const list = Array.isArray(components) ? components : [];
  if (list.length === 0) return out;
  MICRO_KEYS.forEach(function (k) {
    if (list.every(function (c) { return c && c[k] != null; })) {
      out[k] = Math.round(list.reduce(function (a, c) { return a + Number(c[k]); }, 0) * 10) / 10;
    }
  });
  return out;
}

/**
 * Concise title for a grouped basket / grouped custom meal.
 *
 * When EVERY component shares the same restaurant/chain, the parent is titled
 * "<Restaurant> meal" (e.g. "Chick-fil-A meal") — the full item names stay inside
 * ingredient_details only. For mixed-source, mixed-chain, or ordinary
 * non-restaurant baskets we keep the legacy fallback:
 *   - <= 2 items -> "A + B"
 *   - 3+ items   -> "A + N more"
 *
 * @param {Array<{name?: string, restaurant?: string|null}>} components
 * @returns {string}
 */
export function groupedBasketTitle(components) {
  const list = Array.isArray(components) ? components : [];
  const names = list.map((c) => (c && c.name) || '').filter(Boolean);
  const restaurants = list.map((c) => (c && c.restaurant) || null);
  const allHaveRestaurant = list.length > 0 && restaurants.every(Boolean);
  const sameRestaurant = allHaveRestaurant && new Set(restaurants).size === 1;
  if (sameRestaurant) return restaurants[0] + ' meal';
  if (names.length <= 2) return names.join(' + ');
  return names[0] + ' + ' + (names.length - 1) + ' more';
}
